import { defineStore } from 'pinia';
import { ref } from 'vue';
import { vehicleService } from '@/services/api/vehicleService';
import type { Vehicle, VehicleFormData } from '@/types/vehicle';

export const useVehicleStore = defineStore('vehicle', () => {
  // State
  const vehicles = ref<Vehicle[]>([]);
  const selectedVehicle = ref<Vehicle | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Actions
  async function fetchVehicles() {
    loading.value = true;
    error.value = null;

    try {
      vehicles.value = await vehicleService.getVehicles();
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch vehicles';
      console.error('Error fetching vehicles:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchVehicle(vehicleId: number) {
    loading.value = true;
    error.value = null;

    try {
      selectedVehicle.value = await vehicleService.getVehicle(vehicleId);
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch vehicle';
      console.error('Error fetching vehicle:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function createVehicle(data: VehicleFormData, photoFiles: File[] = [], photoSides: string[] = []) {
    loading.value = true;
    error.value = null;

    try {
      const newVehicle = await vehicleService.createVehicle(data, photoFiles, photoSides);
      vehicles.value.unshift(newVehicle); // Add to beginning of array
      return newVehicle;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to create vehicle';
      console.error('Error creating vehicle:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateVehicle(vehicleId: number, data: VehicleFormData, photoFiles: File[] = [], photoSides: string[] = []) {
    loading.value = true;
    error.value = null;

    try {
      const updatedVehicle = await vehicleService.updateVehicle(vehicleId, data, photoFiles, photoSides);

      // Update in the list
      const index = vehicles.value.findIndex(v => v.vehicle_id === vehicleId);
      if (index !== -1) {
        vehicles.value[index] = updatedVehicle;
      }

      // Update selected vehicle if it's the one being updated
      if (selectedVehicle.value?.vehicle_id === vehicleId) {
        selectedVehicle.value = updatedVehicle;
      }

      return updatedVehicle;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update vehicle';
      console.error('Error updating vehicle:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteVehicle(vehicleId: number) {
    loading.value = true;
    error.value = null;

    try {
      await vehicleService.deleteVehicle(vehicleId);
      
      // Remove from the list
      vehicles.value = vehicles.value.filter(v => v.vehicle_id !== vehicleId);
      
      // Clear selected vehicle if it's the one being deleted
      if (selectedVehicle.value?.vehicle_id === vehicleId) {
        selectedVehicle.value = null;
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to delete vehicle';
      console.error('Error deleting vehicle:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  function clearError() {
    error.value = null;
  }

  async function deleteVehiclePhoto(photoId: number) {
    loading.value = true;
    error.value = null;

    try {
      await vehicleService.deleteVehiclePhoto(photoId);
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to delete photo';
      console.error('Error deleting vehicle photo:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  function clearSelectedVehicle() {
    selectedVehicle.value = null;
  }

  return {
    // State
    vehicles,
    selectedVehicle,
    loading,
    error,
    // Actions
    fetchVehicles,
    fetchVehicle,
    createVehicle,
    updateVehicle,
    deleteVehicle,
    deleteVehiclePhoto,
    clearError,
    clearSelectedVehicle
  };
});

